var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import _ from "lodash";
import { s_log } from "./services/log/index.js";
import { RooDate } from "roo-time";
import { config } from "./keykeeper/public.js";
/** Utilities that are NOT specific to the roo domain (for those, see rooUniversalTools.ts). */
/**
 * Throws an exception.  Also demands a parameter of type `never` just for type checking.
 * Useful for compile-time checks that some code is unreachable, when typescript can't notice that fact.
 * @example
 *   function fnA(x: "A"|"B") {
 *     if (x === "A") return;
 *     assertNever(x); // compile time error! And at runtime, will throw an exception.
 *   }
 *   function fnAB(x: "A"|"B") {
 *     if (x === "A" || x === "B") return;
 *     assertNever(x); // no error; confirmed unreachable (but would throw a runtime exception if not)
 *   }
 */
export function assertUnreachable(x) {
    // throw new ImpossibleError(x);
    throw new Error("Impossible");
}
// ----------------------
// findOrError
// ----------------------
export function findOrError(arr, findFn, lid) {
    const index = arr.findIndex(findFn);
    if (index === -1) {
        const msg = `Unable to find value based on fn ${lid} ${JSON.stringify(arr)}`;
        console.error(msg);
        console.log("Array");
        console.log(arr);
        console.log("Fn");
        console.log(JSON.stringify(findFn));
        throw Error(msg);
    }
    return Object.assign(Object.assign({}, arr[index]), { _index: index });
}
// ----------------------
// findObjectKeyOrError
// ----------------------
export function findObjectKeyOrError(obj, key, lid) {
    const x = obj[key];
    if (!x) {
        const msg = "Unable to find object key " + lid;
        console.error(msg, obj, key);
        throw new Error(msg);
    }
    return x;
}
// ----------------------
// convertMapToArray
// ----------------------
export function convertMapToArray(obj) {
    return Object.keys(obj).map((k, index) => (Object.assign({ _key: k, _index: index }, obj[k])));
}
export function convertObjectArrayToMap(p, includeIndexKey) {
    let typeofCheckHistory = "default";
    const errorOnDuplicates = p.errorOnDuplicates === undefined ? true : p.errorOnDuplicates;
    const errorOnInvalidKey = p.errorOnInvalidKey === undefined ? true : p.errorOnInvalidKey;
    const result = {
        map: {},
        itemsWithInvalidKeys: [],
    };
    for (let i = 0; i < p.arr.length; i++) {
        let item = p.arr[i];
        if (includeIndexKey) {
            item = Object.assign({}, item);
            // @ts-ignore
            item._index = i;
        }
        // @ts-ignore
        const keyVal = item[p.key];
        const typeOfResult = typeof keyVal;
        const invalidKeyType = !["string", "number"].includes(typeOfResult);
        if (keyVal === undefined || keyVal === null || invalidKeyType) {
            if (errorOnInvalidKey) {
                console.error(`Invalid key when running convertObjectArrayToMap. ${p.lid} Expected key: ${p.key}.`);
                console.error("Item:");
                console.error(item);
                throw new Error(`Invalid key when running convertObjectArrayToMap ${p.lid}`);
            }
            else {
                result.itemsWithInvalidKeys.push(item);
            }
        }
        else {
            if (typeofCheckHistory === "default") {
                typeofCheckHistory = typeOfResult;
            }
            if (typeofCheckHistory !== typeOfResult) {
                console.warn("Warning: convertObjectArrayToMap has detected string AND number results for key value. This suggest inconsistent data. Numbers and strings will be the same on the final map. Hence '1' and 1 will be mapped to the same node. " +
                    p.lid);
            }
            // @ts-ignore
            if (result[keyVal] && errorOnDuplicates) {
                console.error("Item 1");
                // @ts-ignore
                console.error(result[keyVal]);
                console.error("Item 2");
                console.error(item);
                throw new Error("Multiple items found with the same key " + p.lid);
            }
            else {
                result.map[keyVal] = item;
            }
        }
    }
    return result;
}
// ----------------------
// simplePrimitiveJoin
// ----------------------
export function simplePrimitiveJoin(p) {
    const result = {
        leftOuter: [],
        inner: [],
        rightOuter: [],
    };
    for (let i = 0; i < p.left.length; i++) {
        const item = p.left[i];
        if (item === undefined || item === null) {
            console.warn("Warning: simpleJoin <undefined> or <null> detected. Will ignore. " + p.lid);
            continue;
        }
        if (p.right.indexOf(item) === -1) {
            result.leftOuter.push(item);
        }
        else {
            result.inner.push(item);
        }
    }
    for (let i = 0; i < p.right.length; i++) {
        const item = p.right[i];
        if (item === undefined || item === null) {
            console.warn("Warning: simpleJoin <undefined> or <null> detected. Will ignore. " + p.lid);
            continue;
        }
        if (p.left.indexOf(item) === -1) {
            result.rightOuter.push(item);
        }
    }
    return result;
}
// ----------------------
// simpleObjectJoin
// ----------------------
export function simpleObjectJoin(p) {
    const result = {
        leftOuter: [],
        inner: [],
        rightOuter: [],
    };
    const { map: leftMap, itemsWithInvalidKeys: leftInvalidItems } = convertObjectArrayToMap({
        arr: p.left,
        key: p.leftJoinKey,
        errorOnDuplicates: true,
        errorOnInvalidKey: false,
        lid: p.lid,
    });
    result.leftOuter.push(...leftInvalidItems);
    const { map: rightMap, itemsWithInvalidKeys: rightInvalidItems } = convertObjectArrayToMap({
        arr: p.right,
        key: p.rightJoinKey,
        errorOnDuplicates: true,
        errorOnInvalidKey: false,
        lid: p.lid,
    });
    result.rightOuter.push(...rightInvalidItems);
    const { leftOuter, inner, rightOuter } = simplePrimitiveJoin({
        left: Object.keys(leftMap),
        right: Object.keys(rightMap),
        lid: p.lid,
    });
    leftOuter.forEach((key) => {
        result.leftOuter.push(leftMap[key]);
    });
    rightOuter.forEach((key) => {
        result.rightOuter.push(rightMap[key]);
    });
    inner.forEach((key) => {
        result.inner.push({ left: leftMap[key], right: rightMap[key] });
    });
    return result;
}
// ----------------------
// omitKeysFromObject, extractKeysFromObject, hasOnlyAllowedKeys
// ----------------------
// TODO: getting these type signatures more consistent could be good
export function omitKeysFromObject(p) {
    const newObj = Object.assign({}, p.obj);
    for (let key of p.keys) {
        delete newObj[key];
    }
    return newObj;
}
export function pickKeysFromObject(obj, ...keys) {
    const newObj = {};
    for (let key of keys) {
        if (obj.hasOwnProperty(key)) {
            newObj[key] = obj[key];
        }
    }
    return newObj;
}
export function hasOnlyAllowedKeys(obj, ...allowedKeys) {
    const objKeys = Object.keys(obj);
    return objKeys.every((k) => allowedKeys.includes(k));
}
// ----------------------
// CONSTANTS
// ----------------------
export const CONSTANTS_SHARED_UNIVERSAL = {
    // There are much more complex ones but the reality is they still miss various edge cases
    // Safest way is to use a very simple regex and send them a test email
    emailRegularExpression: /^\S+@\S+\.\S+$/, //
    password: /^(?=.*[a-zA-Z])(?=.*[0-9]).+$/, // 1 number, 1 character
};
// ----------------------
// DEFERRED
// ----------------------
//Generates a promise that ALSO has a `resolve` and `reject` property, to let the promise be resolved elsewhere
export function deferred() {
    let resolve, reject;
    const prom = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });
    prom.resolve = (val) => {
        prom.isResolved = true;
        resolve(val);
    };
    prom.reject = (val) => {
        prom.isRejected = true;
        reject(val);
    };
    Object.assign(prom, {
        isResolved: false,
        isRejected: false,
    });
    return prom;
}
// ----------------------
// RANDOM - very simple random things when a full library is over kill
// ----------------------
/**
 * @param min Minimum value, inclusive
 * @param max Maximum value, inclusive
 */
export function generateRandomInt(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}
const shortOptions = [
    "Bob",
    "Joe",
    "Amy",
    "Kate",
    "Phil",
    "Leo",
    "Leo",
    "Fred",
    "Zion",
    "Zion",
    "John",
    "Dan",
    "Susan",
    "Lisa",
    "Emma",
];
export function generateRandomName() {
    return shortOptions[generateRandomInt(0, shortOptions.length - 1)] || "";
}
export function generateRandomEmail() {
    return `${generateRandomInt(100000, 999999)}@example.com`;
}
// ----------------------
// BASE 64
// ----------------------
const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";
export const Base64 = {
    encode: (input = "", lid) => {
        let str = input;
        let output = "";
        for (let block = 0, charCode, i = 0, map = chars; str.charAt(i | 0) || ((map = "="), i % 1); output += map.charAt(63 & (block >> (8 - (i % 1) * 8)))) {
            charCode = str.charCodeAt((i += 3 / 4));
            if (charCode > 0xff) {
                throw new Error("'btoa' failed: The string to be encoded contains characters outside of the Latin1 range. " + lid);
            }
            block = (block << 8) | charCode;
        }
        return output;
    },
    decode: (input = "", lid) => {
        let str = input.replace(/=+$/, "");
        let output = "";
        if (str.length % 4 == 1) {
            throw new Error("'atob' failed: The string to be decoded is not correctly encoded. " + lid);
        }
        for (let bc = 0, bs = 0, buffer, i = 0; (buffer = str.charAt(i++)); ~buffer && ((bs = bc % 4 ? bs * 64 + buffer : buffer), bc++ % 4)
            ? (output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
            : 0) {
            buffer = chars.indexOf(buffer);
        }
        return output;
    },
};
// ----------------------
// JWT DECODE
// ----------------------
export function decodeTokenPayloadWithoutVerification(token, lid) {
    var base64Url = token.split(".")[1];
    if (!base64Url) {
        throw new Error("Invalid base64Url " + lid);
    }
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(Base64.decode(base64, lid)
        .split("")
        .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
    })
        .join(""));
    return JSON.parse(jsonPayload);
}
export class SimplePromiseQueue {
    constructor(p) {
        this.concurrency = 1;
        this.timeoutMS = 999999999;
        this.wrappedFns = [];
        this.counter = 0;
        this.runningWrappedFns = {};
        this.pendingDrains = [];
        if (p === null || p === void 0 ? void 0 : p.concurrency) {
            this.concurrency = p.concurrency;
        }
        if (p === null || p === void 0 ? void 0 : p.timeoutMS) {
            this.timeoutMS = p.timeoutMS;
        }
    }
    tick() {
        return __awaiter(this, void 0, void 0, function* () {
            const numberOfCurrentlyExecutingFns = this.getNumberOfCurrentlyExecutingFns();
            const numberOfPendingFns = this.getNumberOfPendingFns();
            if (numberOfCurrentlyExecutingFns + numberOfPendingFns === 0) {
                this.pendingDrains.forEach((drain) => {
                    drain();
                });
                this.pendingDrains = [];
            }
            else if (numberOfCurrentlyExecutingFns < this.concurrency) {
                const nextFn = this.wrappedFns.shift();
                if (nextFn) {
                    this.counter += 1;
                    const nextCounter = this.counter;
                    this.runningWrappedFns[`${nextCounter}`] = nextFn;
                    yield this.runningWrappedFns[`${nextCounter}`]();
                    delete this.runningWrappedFns[`${nextCounter}`];
                    this.tick();
                }
            }
        });
    }
    abort() {
        this.wrappedFns = [];
    }
    push(fn, lid, isPriority) {
        const clientPromise = new Promise((clientResolve, clientReject) => {
            const wrappedFn = () => {
                const internalConcurrencyPromise = new Promise((internalResolve) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        const timeout = setTimeout(() => {
                            clientReject(new Error(`SimplePromiseQueue timeout. lid: ${lid} MS: ` + this.timeoutMS));
                            internalResolve();
                        }, this.timeoutMS);
                        const r = yield fn();
                        clearTimeout(timeout);
                        clientResolve(r);
                    }
                    catch (e) {
                        clientReject(e);
                    }
                    finally {
                        internalResolve();
                    }
                }));
                return internalConcurrencyPromise;
            };
            if (isPriority) {
                this.wrappedFns.unshift(wrappedFn);
            }
            else {
                this.wrappedFns.push(wrappedFn);
            }
            this.tick();
        });
        return clientPromise;
    }
    getNumberOfPendingFns() {
        return this.wrappedFns.length;
    }
    getNumberOfCurrentlyExecutingFns() {
        return Object.keys(this.runningWrappedFns).length;
    }
    drain() {
        setTimeout(() => {
            this.tick();
        }, 0);
        return new Promise((res) => {
            this.pendingDrains.push(res);
        });
    }
}
// async function foo() {
//   return {
//     age: 12,
//     name: 'Bob'
//   };
// }
// let cc: ThenArg<ReturnType<typeof foo>> = { age: 1, name: 'Sally' };
export const isEmptyObject = (ob) => Object.keys(ob).length === 0;
// ----------------------
// Date Encoding/Decoding
// ----------------------
export const ENCODED_DATE_KEY = "__encodedDate__";
export function encodeObjReplacingDatesWithPlaceholder(obj, debugContext) {
    var _a, _b;
    if (obj instanceof Date) {
        if (isNaN(obj.getTime())) {
            if (config.ENV === "dev") {
                const errString = `Invalid Date detected during encoding. lid: ${debugContext.lid}, keyPath: ${(_a = debugContext._keyPath) !== null && _a !== void 0 ? _a : "root"}, meta: ${JSON.stringify(debugContext.meta)}, typeof: ${typeof obj}, constructor: ${(_b = obj === null || obj === void 0 ? void 0 : obj.constructor) === null || _b === void 0 ? void 0 : _b.name}, valueOf: ${obj.valueOf()}, Date.toString(): ${obj.toString()}`;
                console.log(errString);
                throw new Error(errString);
            }
        }
        const d = { [ENCODED_DATE_KEY]: obj.toISOString() };
        return d; // T should narrow to Date
    }
    else if (Array.isArray(obj)) {
        const obj2 = obj;
        const a = obj2.map((item, i) => {
            var _a;
            return encodeObjReplacingDatesWithPlaceholder(item, Object.assign(Object.assign({}, debugContext), { _keyPath: `${(_a = debugContext._keyPath) !== null && _a !== void 0 ? _a : "root"}[${i}]` }));
        });
        return a; // T should narrow to Elem[]
    }
    else if (obj && typeof obj === "object") {
        return Object.keys(obj).reduce((acc, key) => {
            var _a;
            const mapped = encodeObjReplacingDatesWithPlaceholder(obj[key], Object.assign(Object.assign({}, debugContext), { _keyPath: `${(_a = debugContext._keyPath) !== null && _a !== void 0 ? _a : "root"}.${key}` }));
            const mapped2 = mapped; // DatesReplaced<T> and T share keys
            acc[key] = mapped2;
            return acc;
        }, {});
    }
    return obj; // T should narrow to (not object), so DatesReplaced<T> is T
}
export function decodeObjReplacingDatePlaceholdersWithDates(obj) {
    if (obj && typeof obj === "object" && ENCODED_DATE_KEY in obj) {
        if (typeof obj[ENCODED_DATE_KEY] !== "string") {
            throw s_log.getSharedLogger().createErrorEnsureEventuallyLogged({
                message: `Encountered wrong type for ${ENCODED_DATE_KEY}`,
                lid: "r-q2gbBu2D",
                meta: obj,
            });
        }
        // obj may be inferred to be an EncodedDate now
        const d = new RooDate(obj[ENCODED_DATE_KEY]);
        return d; // DatesReplaced<T> is EncodedDate implies T is RooDate
    }
    else if (Array.isArray(obj)) {
        const obj2 = obj;
        const a = obj2.map(decodeObjReplacingDatePlaceholdersWithDates);
        return a; // T should narrow to Elem[]
    }
    else if (obj && typeof obj === "object") {
        return Object.keys(obj).reduce((acc, key) => {
            // DatesReplaced<T[keyof T]>
            const val = obj[key];
            const val2 = val; // DatesReplaced<T> and T share keys
            acc[key] = decodeObjReplacingDatePlaceholdersWithDates(val2);
            return acc;
        }, {});
    }
    return obj; // DatesReplaced<T> is T for all non-objects
}
export function jsonStringifyWithDates(obj, space = 2) {
    function replacer(key, value) {
        if (value instanceof Date)
            return value.toISOString();
        return value;
    }
    return JSON.stringify(obj, replacer, space);
}
// ----------------------
// Arithmetic
// ----------------------
export function roundTo2Decimals(amount, 
/** Default is "nearest" */
roundType) {
    return roundToNDecimals(amount, { decimals: 2, roundType });
}
export function roundTo3Decimals(val) {
    return parseFloat(val.toFixed(3));
}
export function roundToNDecimals(value, 
/**
 * Options
 */
options) {
    const o = Object.assign({ decimals: 2, roundType: "nearest" }, options);
    const factor = Math.pow(10, Math.floor(o.decimals));
    switch (o.roundType) {
        case "down":
            return Math.floor(value * factor) / factor;
        case "up":
            return Math.ceil(value * factor) / factor;
        default:
            return Math.round(value * factor) / factor;
    }
}
export function findUniqueKey(obj1, obj2) {
    var _a;
    const uniqueKey = (_a = _.difference(Object.keys(obj1), Object.keys(obj2))[0]) !== null && _a !== void 0 ? _a : _.difference(Object.keys(obj2), Object.keys(obj1))[0];
    return uniqueKey;
}
export function findDifferences(obj1, obj2) {
    const result = {};
    const compare = (o1, o2) => {
        if (typeof o1 !== "object" || typeof o2 !== "object" || o1 === null || o2 === null) {
            return o1 !== o2 ? { value1: o1, value2: o2 } : undefined;
        }
        const diff = {};
        const allKeys = new Set([...Object.keys(o1), ...Object.keys(o2)]);
        allKeys.forEach((key) => {
            const val1 = o1[key];
            const val2 = o2[key];
            if (typeof val1 === "object" && typeof val2 === "object") {
                const nestedDiff = compare(val1, val2);
                if (nestedDiff && Object.keys(nestedDiff).length > 0) {
                    diff[key] = nestedDiff;
                }
            }
            else if (val1 !== val2) {
                diff[key] = { value1: val1, value2: val2 };
            }
        });
        return Object.keys(diff).length > 0 ? diff : undefined;
    };
    const differences = compare(obj1, obj2);
    return differences ? differences : {};
}
export function uriToBlob(uri) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield fetch(uri);
        const blob = yield response.blob();
        return blob;
    });
}
// ----------------------
// Misc
// ----------------------
export function extractAndAssertPresent(item, errorMessageBuilder, fieldname) {
    const x = item === null || item === void 0 ? void 0 : item[fieldname];
    if (x === undefined || x === null) {
        const message = errorMessageBuilder(fieldname);
        throw s_log.getSharedLogger().createErrorEnsureEventuallyLogged({
            message: message,
            lid: "r-SbhVK1PV",
            meta: { item },
        });
    }
    return x;
}
export function extractAndWarnIfAbsent(item, warningMessageBuilder, fieldname) {
    const x = item[fieldname];
    if (x === undefined || x === null) {
        const message = warningMessageBuilder(fieldname);
        s_log.getSharedLogger().warn({ message: message, lid: "r-ZjWdzlD6", meta: { item } });
        return null;
    }
    return x;
}
/**
 * Like _.isEqual, but uses RooDate.valueOf when comparing dates (as strict equality checking
 * fails even for equal dates).
 * TODO: it would be great to get a description of WHERE it doesn't match...
 */
export function isEqualWithDates(obs, exp) {
    if (obs === null || typeof obs !== "object" || exp === null || typeof exp !== "object")
        return obs === exp;
    return _.isEqualWith(obs, exp, (o, e) => {
        if (o instanceof Date && e instanceof Date)
            return o.valueOf() === e.valueOf();
        return undefined;
    });
}
/**
 * Like _.isMatch, but uses RooDate.valueOf when comparing dates (as strict equality checking
 * fails even for equal dates).
 * TODO: it would be great to get a description of WHERE it doesn't match...
 */
export function isMatchWithDates(obs, exp) {
    if (obs === null || typeof obs !== "object" || exp === null || typeof exp !== "object")
        return obs === exp;
    return _.isMatchWith(obs, exp, (o, e) => {
        if (o instanceof Date && e instanceof Date)
            return o.valueOf() === e.valueOf();
        return undefined;
    });
}
/** Like String.includes, but actually narrows the type. */
export function narrowingStringIncludes(str, infix) {
    return str.includes(infix);
}
/** Like List<string>.includes, but actually narrows the type. */
export function narrowingListIncludes(list, val) {
    return list.includes(val);
}
export function objectTypeHasPrefix(item, prefix) {
    return item.type.startsWith(prefix);
}
export function objectTypeHasInfix(item, infix) {
    return item.type.includes(infix);
}
export function objectTypeHasSuffix(item, suffix) {
    return item.type.endsWith(suffix);
}
// For use in forceTimeouts.  Reset on forceTimeouts call.  Individual items also removed each
// time a race promise from addTimeout finishes (where TimeoutCleaningInfo.timeoutRef is used as
// the search key).
let _timeoutCleaningInfo = [];
/**
 * Causes all promises created by addTimeout that haven't already settled to be rejected as though
 * they timed out.
 * Useful mostly for unit tests.
 */
export function _forceTimeouts() {
    _timeoutCleaningInfo.forEach((x) => {
        x.rejecter();
        clearTimeout(x.timeoutRef);
    });
    _timeoutCleaningInfo = [];
}
/** Checks size of _timeoutCleaningInfo.  Used just for unit test to ensure we avoid garbage leaks. */
export function _getAddTimeoutDataSize() {
    return _timeoutCleaningInfo.length;
}
/**
 * Makes a promise that settles when the first of three things happens:
 *  - The given input promise is settled
 *  - The given amount of time passes (rejects using timeoutErrorMaker)
 *  - The forceTimeouts function is called (rejects using timeoutErrorMaker)
 * timeoutErrorMaker will be called at most once, and never after the original promise settles
 * Note: holdNodeEventLoopUntilTimeout will only be respected in a node environment (where
 * setTimeout().unRef is defined).
 */
export function addTimeout(promise, timeoutMs, timeoutErrorMaker, holdNodeEventLoopUntilTimeout = false) {
    let rejectFn;
    let timeoutRef;
    const timeoutPromise = new Promise((_, reject) => {
        rejectFn = () => reject(timeoutErrorMaker());
        timeoutRef = setTimeout(rejectFn, timeoutMs);
        if (!holdNodeEventLoopUntilTimeout && typeof timeoutRef === "object" && Object.hasOwn(timeoutRef, "unref"))
            timeoutRef.unref();
    });
    _timeoutCleaningInfo.push({
        rejecter: rejectFn,
        timeoutRef: timeoutRef,
    });
    return Promise.race([promise, timeoutPromise]).finally(() => {
        var _a;
        // clean up (cancel timeout in case still pending, and clean _timeoutCleaningInfo so it doesn't grow forever)
        const idx = _timeoutCleaningInfo.findIndex((x) => x.timeoutRef === timeoutRef);
        if (idx === -1)
            return;
        clearTimeout((_a = _timeoutCleaningInfo[idx]) === null || _a === void 0 ? void 0 : _a.timeoutRef); // harmless if already called
        _timeoutCleaningInfo.splice(idx, 1);
    });
}
/** Checks whether a string is a valid ISO 8601 date (in yyyy-MM-dd format) */
export const isValidISODate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(RooDate.parse(s));
//# sourceMappingURL=sharedUniversalTools.js.map